import { splitArrayIntoNSubArrays } from '@/utils';
import { asText, Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  InfoColumnsSliceDefault,
  InfoColumnsSliceWithAutoScroll,
  InfoColumnsSliceWithCards,
  InfoColumnsSliceWithFloatingCards,
  InfoColumnsSliceWithSliderCaseStories,
  InfoColumnsSliceWithSliderStories,
} from '../../../prismicio-types';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import AnimatedSlider from '@/components/common/AnimatedSlider';
import { cn } from '@/lib/utils';
import CardSlider, { CardSliderItem } from '@/components/common/CardSlider';
import * as prismic from '@prismicio/client';

enum SLICE_VARIATION {
  default = 'default',
  withAutoScroll = 'withAutoScroll',
  withSliderStories = 'withSliderStories',
  withCards = 'withCards',
  withSliderCaseStories = 'withSliderCaseStories',
  withFloatingCards = 'withFloatingCards',
}

const baseClass =
  'text-white px-5 py-16 md:px-16 md:py-28 max-w-screen-2xl mx-auto';

/**
 * Props for `InfoColumns`.
 */
export type InfoColumnsProps = SliceComponentProps<Content.InfoColumnsSlice>;

/**
 * Component for "InfoColumns" Slices.
 */
const InfoColumns = ({ slice }: InfoColumnsProps): JSX.Element => {
  const getClassByVariation = (variation: string) => {
    switch (variation) {
      case SLICE_VARIATION.default:
        return baseClass;
      case SLICE_VARIATION.withAutoScroll:
      case SLICE_VARIATION.withCards:
        return `bg-[#ffffff]`;
      case SLICE_VARIATION.withSliderStories:
        return `bg-[#f4f4f4]`;
      case SLICE_VARIATION.withSliderCaseStories:
      case SLICE_VARIATION.withFloatingCards:
        return '';
      default:
        return baseClass;
    }
  };

  const styledClass = getClassByVariation(slice.variation);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={styledClass}
    >
      {slice.variation === SLICE_VARIATION.default && <Default slice={slice} />}
      {slice.variation === SLICE_VARIATION.withAutoScroll && (
        <WithAutoScroll slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withSliderStories && (
        <WithSliderStories slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withCards && (
        <WithCards slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withSliderCaseStories && (
        <WithSliderCaseStories slice={slice} />
      )}
      {slice.variation === SLICE_VARIATION.withFloatingCards && (
        <WithFloatingCards slice={slice} />
      )}
    </section>
  );
};

const Default = ({
  slice,
}: {
  slice: InfoColumnsSliceDefault;
}): JSX.Element => {
  let columns = [];
  if (slice.primary.one_column) {
    columns = [slice.primary.details];
  } else {
    columns = splitArrayIntoNSubArrays(slice.primary.details, 2);
  }

  return (
    <div className="flex flex-col md:flex-row gap-x-20 justify-between items-stretch">
      <div className="flex flex-col justify-between flex-1 pb-8 md:pb-0 gap-y-8">
        <PrismicRichText field={slice.primary.title} />
        {asText(slice.primary.description) && (
          <PrismicRichText field={slice.primary.description} />
        )}
        <div className="hidden md:inline-block max-h-[519px] overflow-hidden">
          <PrismicNextImage
            field={slice.primary.picture}
            className="w-full h-full object-cover object-bottom rounded-[20px]"
          />
        </div>
      </div>
      {columns.map((column, columnIndex) => {
        return (
          <div
            key={`column_${columnIndex.toString()}`}
            className="flex flex-col flex-1"
          >
            {column.map((detail, index) => {
              return (
                <div
                  key={`detail_${columnIndex}_${index.toString()}`}
                  className={`${!slice.primary.one_column ? 'border-b-[1px]' : ''} border-primary-light-grey/20 py-4 flex gap-x-4 justify-start items-start`}
                >
                  <PrismicNextImage
                    field={detail.icon}
                    className="self-start"
                  />
                  <div>
                    <h3 className="text-wrap text-2xl font-bold pb-2">
                      {detail.title}
                    </h3>
                    <div className="text-wrap">{detail.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      <div className="md:hidden max-h-[420px] overflow-hidden">
        <PrismicNextImage
          field={slice.primary.picture}
          className="w-full h-full object-cover object-bottom rounded-[20px]"
        />
      </div>
    </div>
  );
};

const WithAutoScroll = ({
  slice,
}: {
  slice: InfoColumnsSliceWithAutoScroll;
}): JSX.Element => {
  return (
    <div className="text-white max-w-screen-2xl mx-auto">
      <div className="w-full mx-auto flex flex-col md:flex-row">
        {/* Left content section */}
        <div className="flex-1 space-y-4 px-5 py-16 md:px-16 md:py-28 ">
          <h1 className="text-2xl md:text-[40px] font-bold text-gray-900 leading-tight">
            <PrismicRichText field={slice.primary.title} />
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl">
            {asText(slice.primary.description) && (
              <PrismicRichText field={slice.primary.description} />
            )}
          </p>
          <PrismicNextLink
            field={slice.primary.button_link}
            className="inline-flex items-center rounded-lg gap-2 bg-primary-shade-green hover:bg-button-gradient text-white px-6 py-3 transition-colors"
          >
            {asText(slice.primary.button_label)}
            <PlainRightArrow
              className="ml-4"
              height={24}
              width={24}
              arrowFill="white"
            />
          </PrismicNextLink>
        </div>

        <div
          className="relative overflow-hidden max-h-[442px] w-full md:max-w-lg
  before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-12 before:bg-gradient-to-b before:from-white before:to-transparent
  after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-12 after:bg-gradient-to-t after:from-white after:to-transparent"
        >
          <div
            className="flex flex-col animate-scroll-vertical space-y-6"
            style={{
              animation: 'scroll-vertical 60s linear infinite',
            }}
          >
            {[...slice.primary.details, ...slice.primary.details].map(
              (column, index) => (
                <div key={`column_${index}`} className="w-full">
                  <div className="bg-white p-8 shadow-md rounded-2xl border border-gray-100 flex items-center gap-6 min-h-[100px] w-full">
                    <div className="flex-shrink-0 w-12 h-12">
                      <PrismicNextImage
                        field={column.icon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-black">
                      {column.title}
                    </h3>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const WithSliderStories = ({
  slice,
}: {
  slice: InfoColumnsSliceWithSliderStories;
}): JSX.Element => {
  return (
    <>
      <div className="text-white max-w-screen-2xl mx-auto">
        <div className="w-full mx-auto flex flex-col md:flex-row">
          <div className="flex-1 space-y-4 px-5 py-16 md:px-16 md:py-12">
            <PrismicNextImage
              className="w-40 h-12 mix-blend-darken rounded-[10px]"
              field={slice.primary.picture}
              alt=""
            />

            <h1 className="text-2xl md:text-[40px] font-bold text-gray-900 leading-tight">
              <PrismicRichText field={slice.primary.title} />
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl">
              {asText(slice.primary.description) && (
                <PrismicRichText field={slice.primary.description} />
              )}
            </p>
            <PrismicNextLink
              field={slice.primary.button_link}
              className="inline-flex items-center rounded-lg gap-2 bg-primary-shade-green hover:bg-button-gradient text-white px-6 py-3 transition-colors"
            >
              {asText(slice.primary.button_label)}
              <PlainRightArrow
                className="ml-4"
                height={24}
                width={24}
                arrowFill="white"
              />
            </PrismicNextLink>
          </div>
          <div className="relative overflow-hidden max-h-[442px] w-full md:max-w-lg"></div>
        </div>
      </div>
      <AnimatedSlider
        details={[...slice.primary.details, ...slice.primary.details]}
      />
    </>
  );
};

const WithCards = ({
  slice,
}: {
  slice: InfoColumnsSliceWithCards;
}): JSX.Element => {
  return (
    <div className="text-white max-w-screen-2xl mx-auto">
      <div className="w-full grid grid-cols-12 px-5 py-16 md:px-16 md:py-28 gap-8">
        <div className="col-span-12 md:col-span-8 flex flex-col justify-center space-y-6 h-full">
          <h1 className="text-2xl md:text-[40px] font-bold text-black leading-tight">
            <PrismicRichText field={slice.primary.title} />
          </h1>
          <p className="text-lg text-black">
            {asText(slice.primary.description) && (
              <PrismicRichText field={slice.primary.description} />
            )}
          </p>
          <div className="w-fit">
            <PrismicNextLink
              field={slice.primary.button_link}
              className="inline-flex items-center rounded-lg gap-2 bg-primary-shade-green hover:bg-button-gradient text-white px-6 py-3 transition-colors"
            >
              {asText(slice.primary.button_label)}
              <PlainRightArrow
                className="ml-4"
                height={24}
                width={24}
                arrowFill="white"
              />
            </PrismicNextLink>
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 space-y-6">
          {slice.primary.details.map((item, index) => {
            const hoverColorClasses = [
              'hover:border-[#D96058]',
              'hover:border-[#7BC0E3]',
              'hover:border-[#FBBE36]',
            ];
            const hoverColor =
              hoverColorClasses[index % hoverColorClasses.length];

            return (
              <div key={`detail_${index}`} className="w-full cursor-pointer">
                <div
                  className={cn(
                    'bg-white rounded-2xl p-6 shadow-[0px_4.32px_64.87px_0px_rgba(0,0,0,0.08)]',
                    'border-l-[3px] border-r-[3px] border-t-[3px] border-b-[8px] border-white',
                    'flex items-start gap-4 transition-all duration-300',
                    hoverColor,
                  )}
                >
                  <div className="flex-shrink-0 text-black font-bold text-xl">
                    →
                  </div>
                  <p className="text-gray-800 font-bold">{item.title}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const WithSliderCaseStories = ({
  slice,
}: {
  slice: InfoColumnsSliceWithSliderCaseStories;
}): JSX.Element => {
  const titleText = prismic.asText(slice.primary.title);
  const details: CardSliderItem[] = slice.primary.details as CardSliderItem[];
  return (
    <div className="text-white max-w-screen-2xl mx-auto">
      <div className="w-full mx-auto flex flex-col md:flex-row">
        <div className="flex-1 space-y-6 px-5 py-16 md:px-16 md:py-1 md:mb-16">
          <CardSlider title={titleText} slides={details} />
        </div>
      </div>
    </div>
  );
};

const WithFloatingCards = ({
  slice,
}: {
  slice: InfoColumnsSliceWithFloatingCards;
}): JSX.Element => {
  return (
    <div className="max-w-screen-2xl mx-auto">
      <div className="w-full grid grid-cols-12 px-5 py-16 md:px-16 md:py-28 gap-8">
        <div className="col-span-12 md:col-span-8 space-y-8">
          <h1 className="text-2xl md:text-6xl font-bold text-white leading-tight">
            <PrismicRichText field={slice.primary.title} />
          </h1>
          <div className="text-lg text-white">
            {asText(slice.primary.description) && (
              <PrismicRichText field={slice.primary.description} />
            )}
          </div>
          <PrismicNextLink
            field={slice.primary.button_link}
            className="inline-flex items-center gap-2 bg-green-1 hover:bg-button-gradient rounded-lg text-black font-bold px-6 py-3 transition-colors"
          >
            {asText(slice.primary.button_label)}
            <PlainRightArrow
              className="ml-4"
              height={24}
              width={24}
              arrowFill="black"
            />
          </PrismicNextLink>
        </div>

        <div className="col-span-12 md:col-span-4 space-y-6">
          {slice.primary.details.map((item, index) => (
            <div
              key={`detail_${index}`}
              className={cn(
                'w-full md:max-w-[300px] cursor-pointer',
                index % 3 === 0
                  ? 'ml-0'
                  : index % 3 === 1
                    ? 'mx-auto'
                    : 'mx-auto mr-0',
              )}
            >
              <div
                className={cn(
                  'p-6 rounded-2xl bg-white/10 backdrop-blur-md',
                  'flex items-center gap-4 shadow-[0px_4.32px_64.87px_0px_rgba(0,0,0,0.08)]',
                  'transition-all duration-300',
                )}
              >
                <div className="text-3xl md:text-4xl font-bold font-manrope">
                  {Array.from(item.heading ?? '').map((char, i) => (
                    <span
                      key={i}
                      className={cn(
                        ['/', '+', '%'].includes(char)
                          ? 'text-green-1'
                          : 'text-white',
                      )}
                    >
                      {char}
                    </span>
                  ))}
                </div>
                <div className="flex flex-col text-white text-sm md:text-base">
                  {(item.title ?? '').split('\n').map((line, i, arr) => (
                    <div
                      key={i}
                      className={cn(
                        i === arr.length - 1 ? 'flex items-center gap-1' : '',
                      )}
                    >
                      {line}
                      {i === arr.length - 1 && item.icon && (
                        <PrismicNextImage
                          field={item.icon}
                          className="w-12 h-6 align-middle"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InfoColumns;
